import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal';
import { useStartImpersonation } from '../../hooks/api/useImpersonation';

const MIN_REASON_LENGTH = 10;

export interface StartImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  org: { id: string; name: string; code: string } | null;
}

/**
 * Required-reason capture before super-admin impersonation begins.
 *
 * The reason gets persisted to the *target* org's audit log so the school
 * can see who looked at their data and why -- a transparency commitment
 * we lean on for POPIA compliance.
 */
const StartImpersonationModal: React.FC<StartImpersonationModalProps> = ({
  isOpen,
  onClose,
  org,
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const navigate = useNavigate();
  const start = useStartImpersonation();

  // Reset form whenever the modal closes -- otherwise reopening it for
  // a different org pre-fills the previous reason.
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setTouched(false);
      start.reset();
    }
  }, [isOpen, start]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON_LENGTH;
  const showError = touched && !isValid;

  const handleStart = async () => {
    if (!org || !isValid) {
      setTouched(true);
      return;
    }
    try {
      await start.mutateAsync({ org_id: org.id, reason: trimmed });
      onClose();
      navigate('/dashboard');
    } catch {
      // Error renders below; mutation state carries the message.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={org ? `View as ${org.name}` : 'Start impersonation'}
      size="md"
      actions={[
        { label: 'Cancel', onClick: onClose, variant: 'secondary' },
        {
          // Stays clickable while the reason is too short -- pressing it
          // surfaces the validation message, which is friendlier than a
          // greyed-out button with no feedback.
          label: start.isPending ? 'Starting…' : 'Start viewing',
          onClick: handleStart,
          variant: 'primary',
          loading: start.isPending,
          disabled: start.isPending,
        },
      ]}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          You're about to view <strong>{org?.name}</strong>'s data as a
          super-admin. The session is <strong>read-only</strong> and capped at
          30 minutes. Your reason will be recorded in this organization's
          audit log so its administrators can see who accessed their data.
        </p>
        <div>
          <label
            htmlFor="impersonation-reason"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Reason (required)
          </label>
          <textarea
            id="impersonation-reason"
            data-testid="impersonation-reason"
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. Investigating support ticket #1234 — missing report"
            className={`w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
              showError
                ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-emerald-500 focus:ring-emerald-500'
            } focus:outline-none focus:ring-1`}
          />
          <div className="flex justify-between mt-1 text-xs">
            <span className={showError ? 'text-rose-600' : 'text-gray-500'}>
              {showError
                ? `Please enter at least ${MIN_REASON_LENGTH} characters.`
                : `Minimum ${MIN_REASON_LENGTH} characters.`}
            </span>
            <span className="text-gray-500">{trimmed.length} chars</span>
          </div>
        </div>
        {start.isError ? (
          <p className="text-sm text-rose-600">
            Failed to start session. Please try again.
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default StartImpersonationModal;
